import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase/client";
import { AgendaEvent } from "@/types";
import { useAuth } from "@/providers/AuthProvider";
import { startOfMonth, endOfMonth, addMonths, subMonths, format, setYear, getYear } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Options for useAgendaEvents hook
 */
interface UseAgendaEventsOptions {
  /** Whether to include private events (team only) */
  includePrivate?: boolean;
  /** Whether to include admin-only events */
  includeAdminOnly?: boolean;
  /** Specific weekend ID to filter by */
  weekendId?: string | null;
  /** Whether to include birthday events */
  includeBirthdays?: boolean;
}

/**
 * Return type for useAgendaEvents hook
 */
interface UseAgendaEventsResult {
  events: AgendaEvent[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  fetchEventsForMonth: (date: Date) => Promise<void>;
}

/**
 * Hook to fetch and manage agenda events
 */
export const useAgendaEvents = (
  options: UseAgendaEventsOptions = {}
): UseAgendaEventsResult => {
  const {
    includePrivate = true,
    includeAdminOnly = false,
    weekendId,
    includeBirthdays = true,
  } = options;

  const { profile, roles, userTeam, activeWeekend } = useAuth();
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const hasFetched = useRef(false);

  const isAdmin = roles.isFamilieAdmin || roles.isSuperAdmin;

  /**
   * Fetch birthday events for a date range
   * Creates virtual calendar events from user profiles with date_of_birth
   */
  const fetchBirthdayEvents = async (startDate: Date, endDate: Date): Promise<AgendaEvent[]> => {
    try {
      // Fetch all profiles with birthdays
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("id, full_name, nickname, date_of_birth, avatar_url")
        .not("date_of_birth", "is", null);

      if (error) throw error;
      if (!profiles || profiles.length === 0) return [];

      const birthdayEvents: AgendaEvent[] = [];
      const currentYear = getYear(new Date());

      // For each profile, check if their birthday falls within the date range
      profiles.forEach((profile) => {
        if (!profile.date_of_birth) return;

        // Parse the birthday and set it to current year
        const birthDate = new Date(profile.date_of_birth);
        const thisYearBirthday = setYear(birthDate, currentYear);
        
        // Also check next year's birthday for December-January transitions
        const nextYearBirthday = setYear(birthDate, currentYear + 1);

        const checkBirthday = (birthdayDate: Date) => {
          if (birthdayDate >= startDate && birthdayDate <= endDate) {
            const displayName = profile.nickname || profile.full_name || "Onbekend";
            const age = getYear(birthdayDate) - getYear(birthDate);

            birthdayEvents.push({
              id: `birthday-${profile.id}-${getYear(birthdayDate)}`,
              weekend_id: null,
              title: `🎂 ${displayName} is jarig!`,
              description: age > 0 ? `${displayName} wordt ${age} jaar` : null,
              location: null,
              location_lat: null,
              location_lng: null,
              starts_at: birthdayDate.toISOString(),
              ends_at: birthdayDate.toISOString(),
              all_day: true,
              category: "custom",
              icon: "gift",
              color: "#F59E0B",
              is_mandatory: false,
              send_reminder: false,
              reminder_minutes_before: 0,
              game_id: null,
              created_by: profile.id,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              event_type: "family",
              visibility: "public",
              team_id: null,
              is_recurring: true,
              recurrence_rule: { type: "yearly" },
              parent_event_id: null,
              has_reveal: false,
              reveal_type: null,
              reveal_target: null,
              reveal_at: null,
              reveal_config: null,
              is_revealed: true,
              secret_title: null,
              secret_description: null,
              lottie_animation: "cake",
              notify_on_create: false,
              notify_on_reveal: false,
              custom_notify_at: null,
            } as AgendaEvent);
          }
        };

        checkBirthday(thisYearBirthday);
        checkBirthday(nextYearBirthday);
      });

      return birthdayEvents;
    } catch (err) {
      console.error("Error fetching birthday events:", err);
      return [];
    }
  };

  /**
   * Fetch events for a specific month
   */
  const fetchEventsForMonth = useCallback(
    async (date: Date) => {
      if (!profile?.id) {
        setError("Niet ingelogd");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Calculate date range with buffer (prev and next month for smoother navigation)
        const monthStart = startOfMonth(subMonths(date, 1));
        const monthEnd = endOfMonth(addMonths(date, 1));

        // Build the query - simplified without joins to avoid errors
        let query = supabase
          .from("agenda_events")
          .select("*")
          .gte("starts_at", monthStart.toISOString())
          .lte("starts_at", monthEnd.toISOString())
          .order("starts_at", { ascending: true });

        // Filter by weekend if specified
        if (weekendId) {
          query = query.eq("weekend_id", weekendId);
        } else if (activeWeekend?.id) {
          // Either matches the active weekend OR is null (personal events)
          query = query.or(`weekend_id.eq.${activeWeekend.id},weekend_id.is.null`);
        } else {
          // If no active weekend, just get events without weekend_id (personal)
          query = query.is("weekend_id", null);
        }

        const { data, error: fetchError } = await query;
        
        

        if (fetchError) {
          throw fetchError;
        }

        // Filter events based on visibility and user permissions
        const filteredEvents = (data as AgendaEvent[]).filter((event) => {
          // Public events are visible to everyone
          if (event.visibility === "public") {
            return true;
          }

          // Team-only events are visible to team members
          if (event.visibility === "team_only") {
            if (!includePrivate) return false;
            // Check if user is in the same team
            return event.team_id === userTeam?.id || event.created_by === profile.id;
          }

          // Admin-only events are visible to admins
          if (event.visibility === "admin_only") {
            if (!includeAdminOnly) return false;
            return isAdmin;
          }

          return true;
        });

        // Handle reveals - hide content if not revealed and user is not admin
        const processedEvents = filteredEvents.map((event) => {
          if (event.has_reveal && !event.is_revealed && !isAdmin) {
            // Return a modified event with hidden content
            return {
              ...event,
              // Keep basic info but hide sensitive details
              description: event.secret_description || null,
              title: event.secret_title || event.title,
              location: event.reveal_target === "location" ? null : event.location,
            };
          }
          return event;
        });

        // Fetch and add birthday events if enabled
        let allEvents = processedEvents;
        if (includeBirthdays) {
          const birthdayEvents = await fetchBirthdayEvents(monthStart, monthEnd);
          allEvents = [...processedEvents, ...birthdayEvents];
        }

        // Sort all events by start date
        allEvents.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());

        setEvents(allEvents);
        setCurrentMonth(date);
      } catch (err: any) {
        console.error("Error fetching events:", err);
        setError(err.message || "Kon evenementen niet laden");
      } finally {
        setIsLoading(false);
      }
    },
    [profile?.id, userTeam?.id, activeWeekend?.id, weekendId, includePrivate, includeAdminOnly, isAdmin]
  );

  /**
   * Refetch current month
   */
  const refetch = useCallback(async () => {
    await fetchEventsForMonth(currentMonth);
  }, [currentMonth, fetchEventsForMonth]);

  /**
   * Initial fetch - only run once when profile is available
   */
  useEffect(() => {
    if (!profile?.id || hasFetched.current) return;
    
    hasFetched.current = true;
    fetchEventsForMonth(new Date());
  }, [profile?.id, fetchEventsForMonth]);

  /**
   * Subscribe to realtime updates
   */
  useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel("agenda_events_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "agenda_events",
        },
        () => {
          // Refetch on any changes
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, refetch]);

  return {
    events,
    isLoading,
    error,
    refetch,
    fetchEventsForMonth,
  };
};

export default useAgendaEvents;

